/**
 * AiToolsHeader — page-level header strip for the AI Tools portal and
 * drafts inbox surfaces (Phase X.3.1).
 *
 * Renders the surface title plus a contextual link strip: portal →
 * Drafts; drafts → AI Tools home. Both routes are gated upstream by
 * canUseAiTools, so the link is always safe to follow.
 *
 * Mobile-responsive: flex-wrap on narrow viewports keeps the title +
 * link visible without horizontal scroll. Matches X.3.1 §6 primitive.
 */

import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface AiToolsHeaderProps {
  tenantSlug: string;
  title: string;
  /**
   * Optional inline link on the right side of the header strip. The
   * Drafts inbox passes "← AI Tools" here. The AI Tools landing page
   * (X.3.4.6) drops this in favor of the standalone MyDraftsPill
   * rendered alongside the header by the page; AiTools omits this
   * prop and renders the pill itself.
   */
  rightLink?: { to: string; label: string };
}

export function AiToolsHeader({ title, rightLink }: AiToolsHeaderProps): ReactNode {
  return (
    <header className="flex flex-wrap items-center justify-between gap-2">
      <h1 className="text-xl font-semibold text-ink">{title}</h1>
      {rightLink && (
        <Link
          to={rightLink.to}
          className="text-sm text-sage-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded"
        >
          {rightLink.label}
        </Link>
      )}
    </header>
  );
}
